import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Paper,
  Badge,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
  Typography,
  Slide,
  Button,
  List,
  ListItemIcon,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from "@mui/icons-material/Close";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import NameLogo from "src/@core/components/icons/Name-Logo";
import EmailVerification from "./EmailVerification";
import ChatSettings from "./ChatSettings";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CancelIcon from "@mui/icons-material/Cancel";
import BusinessVerification from "./BusinessVerification";
import MenuSetup from "./MenuSetup";
import AddMenuItemForm from "./menu-items/AddMenuItemForm";
import {
  setIsShowSetupMenu,
  clearSetupMenuDirectLaunch,
} from "src/redux/slices/AuthSlice";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useQuery } from "react-query";
import CategoryUI from "./menu-items/AddCategory";
import LocationSetup from "./LocationSetup";
import MenuCatalogDialog from "./menu-items/MenuCatalogDialog";
import SubscriptionPlanContent, { useSubscriptionInfo } from "src/@core/components/SubscriptionPlanContent";
import DeactivateSubscriptionDialog from "./DeactivateSubscriptionDialog";
import WalletSetup from "./WalletSetup";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";
import TeamMemberLinkingSetup from "./team-member-linking/TeamMemberLinkingSetup";
import { useDoubleTickWidget } from "src/lib/useDoubleTickWidget";

// Constants
const APPBAR_HEIGHT = 64;
const SIDEBAR_WIDTH = 360;
const DIALOG_MIN_HEIGHT = 600;

// Styled components
const ColoredIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgcolor",
})(({ bgcolor }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: bgcolor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: 12,
  "& svg": {
    fontSize: 18,
    color: "#fff",
  },
}));

const MenuListItem = styled(ListItem)({
  marginBottom: 16,
  padding: 0,
  backgroundColor: "#F3F5F9",
  borderRadius: 4,
});

const MenuButton = styled(ListItemButton)({
  color: "#333",
  borderRadius: 8,
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 12,
  paddingBottom: 12,
});

// Memoized SetupMenuItem component
const SetupMenuItem = React.memo(({ item, index, onClick }) => {
  const { t } = useTranslation("dashboard");
  const IconComponent = item.icon;
  const displayLabel = item.labelKey ? t(item.labelKey) : item.label;

  const handleClick = useCallback(() => {
    onClick(item.path);
  }, [onClick, item.path]);

  return (
    <MenuListItem key={`${item.path}-${index}`}>
      <MenuButton onClick={handleClick}>
        <ColoredIconBox bgcolor={item.color}>
          <IconComponent />
        </ColoredIconBox>
        <ListItemText
          primary={displayLabel}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: 400,
            color: "#333",
          }}
        />
        {item.badge && (
          <Badge
            badgeContent={item.badge}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 10,
                height: 16,
                minWidth: 16,
              },
            }}
          />
        )}
      </MenuButton>
    </MenuListItem>
  );
});

SetupMenuItem.displayName = "SetupMenuItem";

// Memoized SidebarMenuContent component
const SidebarMenuContent = React.memo(
  ({ onItemClick, businessLocationData, handleClose }) => {
    const { t } = useTranslation("dashboard");
    const { isOwnBusiness, hasPermission, isViewOnly } = useBusinessPermissions();

    const steps = [
      {
        path: "email verification",
        labelKey: "setupEmailVerification",
        icon: <MailOutlineIcon />,
        color: "#D16CFF",
        statusIcon: businessLocationData?.business?.isEmailVerified ? (
          <CheckCircleIcon sx={{ color: "#34C759", fontSize: 22 }} />
        ) : (
          <NewReleasesIcon sx={{ color: "#F2A73B", fontSize: 22 }} />
        ),
      },
      {
        path: "add location",
        labelKey: "setupAddLocation",
        icon: <LocationOnIcon />,
        color: "#FF5C5C",
        statusIcon: (
          <AddCircleOutlineIcon sx={{ color: "#A259FF", fontSize: 22 }} />
        ),
      },
      {
        path: "add team member",
        labelKey: "setupAddTeamMember",
        icon: <GroupAddIcon />,
        color: "#FFAA3A",
        statusIcon: (
          <AddCircleOutlineIcon sx={{ color: "#A259FF", fontSize: 22 }} />
        ),
      },
      {
        path: "add menu/catalog",
        labelKey: "setupAddMenuCatalog",
        icon: <MenuBookIcon />,
        color: "#FF6F91",
        statusIcon: (
          <AddCircleOutlineIcon sx={{ color: "#A259FF", fontSize: 22 }} />
        ),
      },
      {
        path: "business chat settings",
        labelKey: "setupBusinessChatSettings",
        icon: <ChatBubbleOutlineIcon />,
        color: "#478AFF",
        statusIcon: (
          <MoreHorizIcon
            sx={{ color: "#3C3C43", opacity: 0.3, fontSize: 22 }}
          />
        ),
      },
      {
        path: "business verification",
        labelKey: "setupBusinessVerification",
        icon: <BusinessCenterIcon />,
        color: "#FFD600",
        statusIcon:
          businessLocationData?.business?.isDocumentVerified === 1 ? (
            <CheckCircleIcon sx={{ color: "#34C759", fontSize: 22 }} />
          ) : businessLocationData?.business?.isDocumentVerified === 0 || businessLocationData?.business?.isDocumentVerified === -1 || businessLocationData?.business?.isDocumentVerified === 2 ? (
            <NewReleasesIcon sx={{ color: "#F2A73B", fontSize: 22 }} />
          ) : (
            <MoreHorizIcon
              sx={{ color: "#3C3C43", opacity: 0.3, fontSize: 22 }}
            />
          ),
      },
      {
        path: "wallet setup",
        labelKey: "setupWalletSetup",
        icon: <AccountBalanceWalletIcon />,
        color: "#34C759",
        statusIcon: (
          <MoreHorizIcon
            sx={{ color: "#3C3C43", opacity: 0.3, fontSize: 22 }}
          />
        ),
      },
      {
        path: "subscription",
        labelKey: "setupSubscription",
        icon: <CardMembershipIcon />,
        color: "#A259FF",
        statusIcon: (
          <MoreHorizIcon
            sx={{ color: "#3C3C43", opacity: 0.3, fontSize: 22 }}
          />
        ),
      },
    ];

    const handleMenuItemClick = useCallback(
      (path) => {
        // Disable these items for employees
        const restrictedItems = ["business verification", "subscription" ,"email verification"];
        if (!isOwnBusiness && restrictedItems.includes(path)) {
          return; // Prevent click for employees
        }
        
        // Disable "add location" if user doesn't have editLocationProfile permission AND viewOnly is false
        // (Disable only when BOTH editLocationProfile is false AND viewOnly is false)
        if (path === "add location") {
          if (!isOwnBusiness && !hasPermission('editLocationProfile') && !isViewOnly()) {
            return; // Prevent click
          }
        }

        // Disable "add team member" if both manageTeamMembers is false AND viewOnly is false
        // Enable if manageTeamMembers is true OR viewOnly is true
        if (path === "add team member") {
          if (!isOwnBusiness && !hasPermission('manageTeamMembers') && !isViewOnly()) {
            return; // Prevent click
          }
        }
        
        // Disable "add menu/catalog" if both manageMenu is false AND viewOnly is false
        // Enable if manageMenu is true OR viewOnly is true
        if (path === "add menu/catalog") {
          if (!isOwnBusiness && !hasPermission('manageMenu') && !isViewOnly()) {
            return; // Prevent click
          }
        }

        // Disable "business chat settings" if both manageChats is false AND viewOnly is false
        if (path === "business chat settings") {
          if (!isOwnBusiness && !hasPermission('manageChats') && !isViewOnly()) {
            return; // Prevent click
          }
        }

        // Disable "wallet setup" if both manageWallet is false AND viewOnly is false
        if (path === "wallet setup") {
          if (!isOwnBusiness && !hasPermission('manageWallet') && !isViewOnly()) {
            return; // Prevent click
          }
        }

        if (path === "email verification") {
          if (!businessLocationData?.business?.isEmailVerified) {
            // Open email verification dialog
            onItemClick?.(path);
          }

          return;
        }
        if (path === "business verification") {
          if (
            businessLocationData?.business?.isDocumentVerified === 0 ||
            businessLocationData?.business?.isDocumentVerified === -1 ||
            businessLocationData?.business?.isDocumentVerified === 1 ||
            businessLocationData?.business?.isDocumentVerified === 2
          ) {
            onItemClick?.(path);
          }

          return;
        }
        // For all other paths
        onItemClick?.(path);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [onItemClick, businessLocationData, isOwnBusiness, hasPermission, isViewOnly]
    );

    return (
      <Box
        // minHeight="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        bgcolor="#fff"
        py={3}
      >
        {/* Logo */}
        <Box mt={3} mb={1}>
          <Avatar
            src="/logo.svg" // Replace with your actual logo path or use <SvgIcon />
            sx={{
              width: 90,
              height: 90,
              bgcolor: "#fff",
              border: "4px solid #A259FF",
              mx: "auto",
              padding: 5,
            }}
          >
            <NameLogo />
          </Avatar>
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          align="center"
          fontWeight="bold"
          letterSpacing={1}
          sx={{
            mb: 0,
            textTransform: "lowercase",
            color: "#000",
            fontFamily: "Inter, Arial, sans-serif",
          }}
        >
          be
          <Box component="span" sx={{ color: "#A259FF" }}>
          ɘ
          </Box>
          mz
        </Typography>
        <Typography
          align="center"
          variant="subtitle2"
          sx={{
            color: "#222",
            fontWeight: 500,
            letterSpacing: 1,
            mb: 2,
            fontSize: 16,
          }}
        >
          {t("businessFallback")}
        </Typography>

        {/* Profile setup subtitle */}
        <Typography
          align="center"
          fontWeight={600}
          sx={{
            color: "#A259FF",
            fontSize: 15,
            textTransform: "none",
          }}
          mb={0.5}
        >
          {t("setupProfileSetup")}
        </Typography>
        <Typography
          align="center"
          sx={{
            color: "#222",
            fontSize: 14,
            opacity: 0.7,
            mb: 3,
          }}
        >
          {t("setupLetsTakeFewSeconds")}
        </Typography>

        {/* Steps */}
        <Paper
          elevation={0}
          sx={{
            px: 0,
            pt: 1,
            pb: 2,
            background: "none",
            width: "100%",
            maxWidth: 350,
            borderRadius: 3,
          }}
        >
          <List disablePadding>
            {steps.map((step, idx) => {
              const restrictedItems = ["business verification", "subscription" ,"email verification"];
              let isDisabled = !isOwnBusiness && restrictedItems.includes(step.path);
              
              // Disable "add location" if user doesn't have editLocationProfile permission AND viewOnly is false
              // (Disable only when BOTH editLocationProfile is false AND viewOnly is false)
              if (step.path === "add location") {
                isDisabled = !isOwnBusiness && !hasPermission('editLocationProfile') && !isViewOnly();
              }

              // Disable "add team member" if both manageTeamMembers is false AND viewOnly is false
              // Enable if manageTeamMembers is true OR viewOnly is true
              if (step.path === "add team member") {
                isDisabled = !isOwnBusiness && !hasPermission('manageTeamMembers') && !isViewOnly();
              }
              
              // Disable "add menu/catalog" if both manageMenu is false AND viewOnly is false
              // Enable if manageMenu is true OR viewOnly is true
              if (step.path === "add menu/catalog") {
                isDisabled = !isOwnBusiness && !hasPermission('manageMenu') && !isViewOnly();
              }
              
              // Disable "business chat settings" if both manageChats is false AND viewOnly is false
              if (step.path === "business chat settings") {
                isDisabled = !isOwnBusiness && !hasPermission('manageChats') && !isViewOnly();
              }
              
              // Disable "wallet setup" if both manageWallet is false AND viewOnly is false
              if (step.path === "wallet setup") {
                isDisabled = !isOwnBusiness && !hasPermission('manageWallet') && !isViewOnly();
              }
              
              return (
              <ListItem
                key={step.path}
                sx={{
                  mb: 1,
                  background: isDisabled ? "#F0F0F0" : "#F7F8FA",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.7,
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  minHeight: 56,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor: isDisabled ? "#F0F0F0" : "#E8E8E8",
                  },
                }}
                secondaryAction={
                  <Box display="flex" alignItems="center" minWidth={32}>
                    {step.statusIcon}
                  </Box>
                }
                onClick={() => !isDisabled && handleMenuItemClick(step.path)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 44,
                    mr: 1,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: step.color,
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(step.icon, {
                      style: { color: "#fff", fontSize: 22 },
                    })}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t(step.labelKey)}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: 15,
                    color: isDisabled ? "#999" : "#222",
                    textTransform: "none",
                    fontFamily: "Inter, Arial, sans-serif",
                  }}
                />
              </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Dashboard Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleClose}
          sx={{
            mt: 3,
            bgcolor: "#A259FF",
            color: "#fff",
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 2,
            boxShadow: "none",
            textTransform: "none",
            maxWidth: 250,
            py: 1.2,
            "&:hover": { bgcolor: "#8938e9" },
          }}
        >
          {t("setupDashboard")}
        </Button>
      </Box>
    );
  }
);

SidebarMenuContent.displayName = "SidebarMenuContent";

// Memoized CollapsibleSidebar component
const CollapsibleSidebar = React.memo(
  ({
    maximized,
    onItemClick,
    isDrawerOpen,
    onToggleDrawer,
    openVerifyDialog,
    setOpenVerifyDialog,
    businessLocationData,
    refetchBusinessLocationData,
    handleClose,
    selectedBusinessId,
    selectedPath,
    setSelectedPath,
    showPlanSelection,
    setShowPlanSelection,
    isDowngradeMode,
    setIsDowngradeMode,
    directMenuSetupLaunch,
    onDirectMenuSetupConsumed,
  }) => {
    const [openMenuCatalogDialog, setOpenMenuCatalogDialog] = useState(false);
    const [menuCatalogValue, setMenuCatalogValue] = useState("");
    const [menuCatalogType, setMenuCatalogType] = useState("");
    const activeStepRef = useRef(0); // Preserve step across mounts
    const directCatalogType = directMenuSetupLaunch?.catalogType;

    useEffect(() => {
      if (!directCatalogType) return;
      setMenuCatalogType(directCatalogType);
      setMenuCatalogValue(directCatalogType);
      setOpenMenuCatalogDialog(false);
      setSelectedPath?.("add menu/catalog");
      if (!maximized) onToggleDrawer(false);
      onDirectMenuSetupConsumed?.();
    }, [
      directCatalogType,
      maximized,
      onToggleDrawer,
      setSelectedPath,
      onDirectMenuSetupConsumed,
    ]);

    const handleItemClick = useCallback(
      (path) => {
        onItemClick?.(path);
        setSelectedPath?.(path);
        if (path === "add menu/catalog") {
          setOpenMenuCatalogDialog(true);
          return; // Don't set selectedPath yet, wait for dialog selection
        }

        if (path === "add location") {
          activeStepRef.current = 0; // Reset step if needed
        }

        if (!maximized) {
          onToggleDrawer(false);
        }
      },
      [onItemClick, maximized, onToggleDrawer, setSelectedPath]
    );

    const handleMenuCatalogProceed = useCallback(() => {
      setOpenMenuCatalogDialog(false);
      // Set the selected path to show MenuSetup
      setSelectedPath?.("add menu/catalog");
      if (!maximized) {
        onToggleDrawer(false);
      }
    }, [maximized, onToggleDrawer, setSelectedPath]);

    if (maximized) {
      return (
        <>
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              height: "100%",
              borderRight: "1px solid #e0e0e0",
            }}
          >
            <SidebarMenuContent
              businessLocationData={businessLocationData}
              onItemClick={handleItemClick}
              handleClose={handleClose}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              bgcolor: "background.default",
              display: "flex",
              alignItems: "flex-start", // Changed from 'center'
              justifyContent: "center",
              p: { xs: 2, sm: 3, md: 4 },
              overflow: "auto", // Added overflow auto
              height: "100%", // Added height
              width: "100%",
            }}
          >
            {selectedPath === "add location" && (
              <LocationSetup
                businessLocationData={businessLocationData}
                activeStepRef={activeStepRef} // Pass ref down
                IsMaxView={false}
              />
            )}
            {selectedPath === "add team member" && (
              <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                <TeamMemberLinkingSetup isEmbedded={true} />
              </Box>
            )}
            {selectedPath === "email verification" && (
              <EmailVerification
                isMaxView={true}
                openVerifyDialog={openVerifyDialog}
                setOpenVerifyDialog={setOpenVerifyDialog}
              />
            )}
            {selectedPath === "business chat settings" && (
              <ChatSettings IsMaxView={true} />
            )}
            {selectedPath === "business verification" && (
              <BusinessVerification businessData={businessLocationData} onSuccess={refetchBusinessLocationData}/>
            )}
            {selectedPath === "add menu/catalog" && <MenuSetup catalogType={menuCatalogType}/>}
            {selectedPath === "subscription" && (
              <SubscriptionPlanContent
                businessId={selectedBusinessId}
                currentPlanName={businessLocationData?.business?.subscriptionStatus}
                IsMaxView={false}
                forceShowPlanSelection={showPlanSelection}
                onForceShowPlanSelectionUsed={() => {
                  setShowPlanSelection(false);
                  // Don't reset isDowngradeMode here - let it persist until user navigates away
                }}
                isDowngradeMode={isDowngradeMode}
              />
            )}
            {selectedPath === "wallet setup" && (
              <WalletSetup
                businessLocationData={businessLocationData}
                IsMaxView={true}
              />
            )}
            {/* {selectedPath === 'add menu/catalog' && <AddMenuItemForm IsMaxView={true}/>} */}
            {/* {selectedPath === 'add menu/catalog' && <CategoryUI IsMaxView={true}/>} */}
          </Box>
       
       <MenuCatalogDialog
          open={openMenuCatalogDialog}
          onClose={() => {
            setOpenMenuCatalogDialog(false);
          }}
          value={menuCatalogValue}
          type={menuCatalogType}
          onChange={(value, type) => {
            setMenuCatalogValue(value);
            setMenuCatalogType(type);
          }}
          onProceed={handleMenuCatalogProceed}
        />
        </>
      );
    }

    return (
      <Box sx={{ position: "relative", width: "100%", height: "100vh" }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "#fff",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-start", // Changed from 'center'
              justifyContent: "center",
              overflow: "auto", // Added overflow auto
              height: "100%", // Added height
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari, Edge
              },
            }}
          >
            {selectedPath === "add location" && (
              <LocationSetup
                businessLocationData={businessLocationData}
                activeStepRef={activeStepRef} // Pass ref down
                IsMaxView={true}
              />
            )}
            {selectedPath === "add team member" && (
              <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, p: { xs: 2, sm: 3, md: 4 } }}>
                <TeamMemberLinkingSetup isEmbedded={true} />
              </Box>
            )}
            {selectedPath === "email verification" && (
              <EmailVerification
                isMaxView={false}
                openVerifyDialog={openVerifyDialog}
                setOpenVerifyDialog={setOpenVerifyDialog}
              />
            )}

            {selectedPath === "business chat settings" && (
              <ChatSettings IsMaxView={false} />
            )}
            {selectedPath === "business verification" && (
              <BusinessVerification businessData={businessLocationData}/>
            )}
            {selectedPath === "add menu/catalog" && <MenuSetup catalogType={menuCatalogType} />}
            {selectedPath === "subscription" && (
              <SubscriptionPlanContent
                businessId={selectedBusinessId}
                currentPlanName={businessLocationData?.business?.subscriptionStatus}
                IsMaxView={true}
                forceShowPlanSelection={showPlanSelection}
                onForceShowPlanSelectionUsed={() => {
                  setShowPlanSelection(false);
                  // Don't reset isDowngradeMode here - let it persist until user navigates away
                }}
                isDowngradeMode={isDowngradeMode}
              />
            )}
            {selectedPath === "wallet setup" && (
              <WalletSetup
                businessLocationData={businessLocationData}
                IsMaxView={false}
              />
            )}
            {/* {selectedPath === 'add menu/catalog' && <AddMenuItemForm />} */}
            {/* {selectedPath === 'add menu/catalog' && <CategoryUI IsMaxView={false}/>} */}
          </Box>
        </Box>

        {isDrawerOpen && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
            }}
            onClick={() => onToggleDrawer(false)}
          />
        )}

        <Slide direction="right" in={isDrawerOpen} mountOnEnter unmountOnExit>
          <Paper
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1300,
              bgcolor: "background.paper",
              boxShadow: 3,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <SidebarMenuContent
              onItemClick={handleItemClick}
              businessLocationData={businessLocationData}
              handleClose={handleClose}
            />
          </Paper>
        </Slide>

        <MenuCatalogDialog
          open={openMenuCatalogDialog}
          onClose={() => {
            setOpenMenuCatalogDialog(false);
          }}
          value={menuCatalogValue}
          type={menuCatalogType}
          onChange={(value, type) => {
            setMenuCatalogValue(value);
            setMenuCatalogType(type);
          }}
          onProceed={handleMenuCatalogProceed}
        />
      </Box>
    );
  }
);

CollapsibleSidebar.displayName = "CollapsibleSidebar";

// Main component
const SetupDialog = ({ minWidth = 400, maxWidth = "90vw" }) => {
  const { t } = useTranslation("dashboard");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isShowSetupMenu = useSelector((state) => state.auth.isShowSetupMenu);
  const setupMenuDirectLaunch = useSelector(
    (state) => state.auth.setupMenuDirectLaunch
  );
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );

  // Initialize chat widget only when dialog is open
  // Note: Widget will be initialized in RegisterStepAccount for registration flow
  // This is only for the account setup popup
  useDoubleTickWidget();

  const [maximized, setMaximized] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [isDowngradeMode, setIsDowngradeMode] = useState(false);

  const theme = useTheme();

  const inputsRef = React.useRef("");

  const handleDirectMenuSetupConsumed = useCallback(() => {
    inputsRef.current = "add menu/catalog";
    dispatch(clearSetupMenuDirectLaunch());
  }, [dispatch]);

  const handleClose = () => {
    dispatch(clearSetupMenuDirectLaunch());
    dispatch(setIsShowSetupMenu(false));
  };

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow850 = useMediaQuery("(max-width:850px)");

  const handleMaximize = useCallback(() => setMaximized(true), []);
  const handleMinimize = useCallback(() => setMaximized(false), []);

 const { data: businessLocationData, refetch: refetchBusinessLocationData } = useQuery(
        ["businessLocationData", selectedBusinessId],
        async ({ signal }) => {
            const res = await axiosInstance.get(
                `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`,
                {
                    signal,
                }
            );
            return res.data.data;
        },
        {
            refetchOnWindowFocus: false,
            cacheTime: 30 * 60 * 1000,
            enabled: !!selectedBusinessId && isShowSetupMenu, // Only fetch if selectedBusinessId is available
        }
    );

  const { data: subscriptionInfoData } = useSubscriptionInfo(
    selectedBusinessId,
    selectedPath === "subscription"
  );
  const cancelAtPeriodEnd = subscriptionInfoData?.data?.data?.cancelAtPeriodEnd || false;


  const handleToggleDrawer = useCallback(
    (value) => {
      const newDrawerState = typeof value === "boolean" ? value : !isDrawerOpen;
      setIsDrawerOpen(newDrawerState);
      // When opening the drawer (going back to list view), reset selectedPath and downgrade mode
      if (newDrawerState) {
        setSelectedPath(null);
        setIsDowngradeMode(false);
      }
    },
    [isDrawerOpen]
  );

  // Auto-minimize when screen width goes below 850px
  useEffect(() => {
    if (isBelow850) {
      setMaximized(false);
      if (inputsRef.current !== "") {
        setIsDrawerOpen(false);
      }
    }
  }, [isBelow850]);

  const handleItemClick = useCallback((path) => {
    console.log("Item clicked:", path);
    inputsRef.current = path; // Store the clicked path in the ref
    setSelectedPath(path);
    // Reset downgrade mode when navigating to a different path
    if (path !== "subscription") {
      setIsDowngradeMode(false);
    }
    // Add your navigation logic here
  }, []);

  const handleMenuClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDeactivate = useCallback(() => {
    handleMenuClose();
    setDeactivateDialogOpen(true);
  }, [handleMenuClose]);

  const handleDeactivateDialogClose = useCallback(() => {
    setDeactivateDialogOpen(false);
  }, []);

  const handleDowngradePlan = useCallback(() => {
    handleMenuClose();
    setIsDowngradeMode(true);
    setShowPlanSelection(true);
  }, [handleMenuClose]);

  const dialogPaperProps = useMemo(
    () => ({
      sx: {
        ...(maximized
          ? {
              position: "fixed",
              top: `${APPBAR_HEIGHT}px`,
              left: 0,
              width: "100vw",
              height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
              maxWidth: "100vw",
              maxHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
              borderRadius: 0,
              m: 0,
            }
          : {
              m: 2,
              width: minWidth,
              maxWidth,
              borderRadius: 4,
              minHeight: DIALOG_MIN_HEIGHT,
              maxHeight: "85vh",
              overflowY: "auto",
            }),
        // This hides scrollbar visually (still scrollable)
        scrollbarWidth: "none", // Firefox
        "&::-webkit-scrollbar": {
          display: "none", // Chrome, Safari, Edge
        },
      },
    }),
    [maximized, minWidth, maxWidth]
  );

  return (
    <Dialog
      open={isShowSetupMenu}
      onClose={handleClose}
      maxWidth={false}
      fullScreen={fullScreen}
      PaperProps={dialogPaperProps}
    >
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: "none",
          background: "transparent",
          overflow: "hidden",
        }}
        elevation={0}
      >
        {/* Dialog Controls */}
        {/* Dialog Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            pt: 4,
            pr: 4,
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 20,
            backgroundColor: "transparent",
          }}
        >
          {/* Three-dot menu icon - show when subscription is active and not showing list view */}
          {selectedPath === "subscription" && (maximized || !isDrawerOpen) && (
            <IconButton
              onClick={handleMenuClick}
              size="small"
              aria-label={t("setupSubscriptionMenu")}
              sx={{ mr: 1 }}
            >
              <MoreVertIcon />
            </IconButton>
          )}

          {/* Only show CropSquareIcon if NOT xs */}

          <Box display={{ xs: "none", sm: "flex" }}>
            {!maximized && (
              <IconButton
                onClick={maximized ? handleMinimize : handleMaximize}
                size="small"
                aria-label={maximized ? t("setupMinimize") : t("setupMaximize")}
              >
                <CropSquareIcon />
              </IconButton>
            )}
          </Box>

          {/* Always show RemoveIcon when maximized */}
          {maximized && (
            <IconButton
              onClick={handleMinimize}
              size="small"
              aria-label={t("setupMinimize")}
            >
              <RemoveIcon />
            </IconButton>
          )}

          {/* Close Icon always visible */}
          <IconButton onClick={handleClose} size="small" aria-label={t("setupClose")}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Subscription Menu */}
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 120,
              borderRadius: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              "& .MuiMenuItem-root": {
                fontSize: 14,
                px: 2,
                py: 1.5,
                textTransform: "lowercase",
              },
            },
          }}
        >
          {!cancelAtPeriodEnd && (
            <MenuItem onClick={handleDeactivate}>deactivate</MenuItem>
          )}
          <MenuItem onClick={handleDowngradePlan}>downgrade</MenuItem>
        </Menu>

        {!maximized && !isDrawerOpen && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              pt: 4,
              pr: 4,
              position: "absolute",
              left: 10,
              top: 0,
              zIndex: 20,
            }}
          >
            <IconButton
              sx={{ color: "primary.main" }}
              onClick={handleToggleDrawer}
              size="small"
              aria-label={t("setupToggleDrawer")}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        )}

        {/* Dialog Content */}
        <DialogContent
          sx={{
            p: maximized ? 0 : 1,
            flex: 1,
            display: "flex",
            flexDirection: maximized ? "row" : "column",
            overflow: "auto",
            position: "relative",
            mt: 12, // Consistent top margin to account for control buttons
            scrollbarWidth: "none", // Firefox
            "&::-webkit-scrollbar": {
              display: "none", // Chrome, Safari, Edge
            },
          }}
        >
          <CollapsibleSidebar
            maximized={maximized}
            onItemClick={handleItemClick}
            isDrawerOpen={isDrawerOpen}
            onToggleDrawer={handleToggleDrawer}
            openVerifyDialog={openVerifyDialog}
            setOpenVerifyDialog={setOpenVerifyDialog}
            businessLocationData={businessLocationData}
            refetchBusinessLocationData={refetchBusinessLocationData}
            selectedBusinessId={selectedBusinessId}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
            showPlanSelection={showPlanSelection}
            setShowPlanSelection={setShowPlanSelection}
            isDowngradeMode={isDowngradeMode}
            setIsDowngradeMode={setIsDowngradeMode}
            handleClose={handleClose}
            directMenuSetupLaunch={setupMenuDirectLaunch}
            onDirectMenuSetupConsumed={handleDirectMenuSetupConsumed}
          />
        </DialogContent>
      </Paper>

      {/* Deactivate Subscription Dialog */}
      <DeactivateSubscriptionDialog
        open={deactivateDialogOpen}
        onClose={handleDeactivateDialogClose}
        selectedBusinessId={selectedBusinessId}
      />
    </Dialog>
  );
};

export default SetupDialog;
